import { rotuloDoMotivo } from '../../data/estoque';
import { rotuloDoMotivoDeSemen } from '../../data/semen';

/**
 * O QUE UM CLIENTE TEM GUARDADO, E O QUE JÁ ENTROU E SAIU.
 *
 * Duas perguntas diferentes, e é por isso que são duas seções e não uma lista:
 *
 *   SALDO    "quantos embriões meus estão no botijão hoje?" É um retrato, não
 *            tem data, e não muda quando se escolhe outro período.
 *   EXTRATO  "o que aconteceu com o material do meu cliente?" É uma linha do
 *            tempo, e serve para explicar por que o saldo é o que é.
 *
 * O saldo sem o extrato não se defende: o cliente liga dizendo que mandou 20
 * doses e o sistema mostra 3, e a única resposta possível é "confia". O extrato
 * sem o saldo obriga quem lê a somar a coluna de cabeça.
 *
 * O ENDEREÇO DA PALHETA NÃO ENTRA — nem botijão, nem caneca. É a mesma razão
 * pela qual ele não chega ao portal: é mapa interno do laboratório, muda quando
 * alguém arruma o botijão, e este material vai para a mão do cliente. Quem
 * precisa do endereço para ir buscar usa a aba de Estoque, que é onde ele mora.
 */

/** Dose aceita meia: somar em ponto flutuante rende 2.9999999. */
const emDoses = (v) => Math.round((Number(v) || 0) * 100) / 100;

const dia = (v) => (v ? String(v).slice(0, 10) : null);

/**
 * Embriões congelados agrupados por doadora × touro.
 *
 * O congelado NÃO TEM NÚMERO desde que a numeração passou a ser só do que vai a
 * campo: a palheta no botijão se acha pelo endereço, que não vem para cá. Então
 * listar um por linha daria oito linhas idênticas sem nada que as distinga.
 *
 * O cruzamento é o que o cliente reconhece e o que ele pede pelo telefone:
 * "manda dois da RENASCENÇA com GALATES".
 */
export const saldoDeEmbrioes = (congelados = []) => {
  const mapa = new Map();

  for (const e of congelados || []) {
    const doadora = e?.doadora || '—';
    const touro = e?.touro || '—';
    const k = `${doadora} ${touro}`;
    if (!mapa.has(k)) {
      mapa.set(k, { doadora, touro, quantidade: 0, estagios: new Set(), metodos: new Set() });
    }
    const g = mapa.get(k);
    g.quantidade += 1;
    // `stage` na view do laboratório, `estagio` na do portal: o mesmo dado com
    // dois nomes, e quem chama não deveria ter que saber de qual veio.
    const estagio = e?.stage || e?.estagio;
    if (estagio) g.estagios.add(estagio);
    if (e?.metodo) g.metodos.add(e.metodo);
  }

  return [...mapa.values()]
    .map(g => ({ ...g, estagios: [...g.estagios].sort(), metodos: [...g.metodos].sort() }))
    .sort((a, b) => b.quantidade - a.quantidade
      || a.doadora.localeCompare(b.doadora)
      || a.touro.localeCompare(b.touro));
};

/**
 * Doses de sêmen por partida, só o que tem saldo.
 *
 * A partida ZERADA fica fora daqui e aparece no extrato. No livro do
 * laboratório ela continua na lista, porque lá é história — quem confere precisa
 * ver que as doses existiram e acabaram. Aqui a pergunta é "o que eu TENHO", e
 * zero não é ter.
 */
export const saldoDeSemen = (partidas = []) =>
  (partidas || [])
    .filter(p => emDoses(p?.saldo_doses ?? p?.doses) > 0)
    .map(p => ({
      semen_batch_id: p.semen_batch_id,
      touro: p.touro || '—',
      codigo: p.codigo || null,
      fornecedor: p.fornecedor || null,
      doses: emDoses(p.saldo_doses ?? p.doses),
      desde: dia(p.primeira_entrada || p.data_entrada),
    }))
    .sort((a, b) => b.doses - a.doses || a.touro.localeCompare(b.touro));

/** Os números do alto: o que o cliente tem, em uma linha. */
export const resumoDoEstoqueDoCliente = ({ congelados = [], partidas = [] } = {}) => {
  const semen = saldoDeSemen(partidas);
  return {
    embrioes: (congelados || []).length,
    cruzamentos: saldoDeEmbrioes(congelados).length,
    doses: emDoses(semen.reduce((t, p) => t + p.doses, 0)),
    partidas: semen.length,
    vazio: (congelados || []).length === 0 && semen.length === 0,
  };
};

/**
 * QUEM TEM MATERIAL GUARDADO, e quanto de cada coisa.
 *
 * É a lista que abre a visão por cliente do Estoque: a pergunta ali é "de quem é
 * o que está no botijão", e ela se responde antes de escolher um nome.
 *
 * SÓ QUEM TEM ALGUMA COISA. A carteira inteira nesta lista seria catorze linhas
 * para mostrar que dez estão zeradas — e o Estoque é a tela de quem está com a
 * mão no nitrogênio, não um cadastro.
 *
 * Ordena pelo VOLUME e não pelo nome: quem tem uma caneca inteira aparece antes
 * de quem tem uma palheta, porque é dele que se fala. Embrião e dose não somam
 * entre si — são unidades diferentes —, então o critério é embrião primeiro, que
 * é o material que o laboratório produziu.
 */
export const clientesComEstoque = (congelados = [], partidas = [], clients = []) => {
  const porId = new Map();

  const entrada = (id, nome) => {
    const k = id || nome || 'sem-cliente';
    if (!porId.has(k)) {
      porId.set(k, {
        client_id: id || null,
        // O nome do cadastro manda; o denormalizado na view é a reserva de quem
        // já não está mais na carteira.
        client_name: clients.find(c => c.id === id)?.name || nome || 'Sem cliente',
        embrioes: 0,
        doses: 0,
      });
    }
    return porId.get(k);
  };

  for (const e of congelados || []) {
    entrada(e?.client_id, e?.client_name).embrioes += 1;
  }

  for (const p of partidas || []) {
    const saldo = emDoses(p?.saldo_doses ?? p?.doses);
    if (saldo <= 0) continue;
    const x = entrada(p?.client_id, p?.dono);
    x.doses = emDoses(x.doses + saldo);
  }

  return [...porId.values()]
    .filter(x => x.embrioes > 0 || x.doses > 0)
    .sort((a, b) => b.embrioes - a.embrioes
      || b.doses - a.doses
      || a.client_name.localeCompare(b.client_name));
};

/**
 * O EXTRATO: uma linha por movimento, o mais recente em cima.
 *
 * Extrato se lê de cima para baixo, como o do banco. Quem abre isto está
 * respondendo a uma pergunta que acabou de ser feita ao telefone, e a resposta
 * quase sempre é o último movimento.
 *
 * O QUE ESTE EXTRATO NÃO FECHA, e é honesto dizer: o embrião que foi congelado e
 * já saiu aparece na saída e não na entrada. A entrada de embrião é a data de
 * congelamento, e ela vive na palheta — quando a palheta sai do botijão, a view
 * de saídas não carrega mais essa data. Somar as entradas e subtrair as saídas
 * não devolve o saldo nesse caso, e o PDF escreve isso em vez de deixar quem lê
 * descobrir na conta.
 *
 * O sêmen fecha, porque o livro dele é de movimentos de verdade: cada entrada e
 * cada saída tem linha própria, inclusive o que a bancada descongelou na FIV.
 */
export const extratoDeEstoque = (
  { congelados = [], saidas = [], movimentos = [], partidas = [] } = {},
) => {
  const linhas = [];

  /*
   * ── Embriões que entraram no botijão ──────────────────────────────────
   *
   * Agrupados por dia e cruzamento: congelar uma caneca de oito palhetas é UM
   * movimento, e oito linhas iguais só fariam o extrato mais longo.
   *
   * A DATA CAI PARA A DO SERVIÇO quando a do congelamento não foi gravada, e a
   * linha sai marcada como aproximada. Em produção são 61 palhetas no botijão e
   * ZERO com `freeze_date` — o campo existe e é preenchido junto com o endereço,
   * na arrumação do botijão, que ninguém fez ainda. Exigir a data exata deixaria
   * o extrato vazio justamente onde ele tem mais a explicar; usá-la em silêncio
   * afirmaria um dia que ninguém registrou. Então usa e diz que usou.
   */
  const entradas = new Map();
  for (const e of congelados || []) {
    const exata = dia(e?.freeze_date);
    const data = exata || dia(e?.servico_data);
    if (!data) continue;   // sem data nenhuma não há linha do tempo em que pôr
    const k = `${data}|${e?.doadora || '—'}|${e?.touro || '—'}|${e?.servico_numero || ''}`;
    if (!entradas.has(k)) {
      entradas.set(k, {
        data,
        tipo: 'entrada',
        material: 'embriao',
        quantidade: 0,
        titulo: 'Congelado',
        descricao: `${e?.doadora || '—'} × ${e?.touro || '—'}`,
        detalhe: e?.servico_numero ? `serviço ${e.servico_numero}` : null,
        aproximada: !exata,
      });
    }
    entradas.get(k).quantidade += 1;
  }
  linhas.push(...entradas.values());

  // ── Embriões que saíram ────────────────────────────────────────────────
  const saidasAgrupadas = new Map();
  for (const s of saidas || []) {
    const data = dia(s?.saida_em);
    const k = `${data}|${s?.motivo || ''}|${s?.destino || ''}|${s?.doadora || '—'}|${s?.touro || '—'}`;
    if (!saidasAgrupadas.has(k)) {
      saidasAgrupadas.set(k, {
        data,
        tipo: 'saida',
        material: 'embriao',
        quantidade: 0,
        titulo: rotuloDoMotivo(s?.motivo) || 'Saída',
        descricao: `${s?.doadora || '—'} × ${s?.touro || '—'}`,
        detalhe: s?.destino || null,
      });
    }
    saidasAgrupadas.get(k).quantidade += 1;
  }
  linhas.push(...saidasAgrupadas.values());

  // ── Sêmen: o livro de movimentos, que já vem um por lançamento ─────────
  const partidaPorId = new Map((partidas || []).map(p => [p.semen_batch_id, p]));
  for (const m of movimentos || []) {
    const p = partidaPorId.get(m?.semen_batch_id);
    const rotulo = [p?.touro || '—', p?.codigo].filter(Boolean).join(' · ');
    /*
     * `origem` distingue a saída lançada na tela da que a FIV consumiu.
     * Sem isso, "3 doses saíram" não diz se alguém vendeu ou se a bancada
     * descongelou — e são conversas diferentes com o dono do sêmen.
     */
    const daBancada = m?.origem === 'fiv';
    linhas.push({
      data: dia(m?.data),
      tipo: m?.tipo === 'entrada' ? 'entrada' : 'saida',
      material: 'semen',
      quantidade: emDoses(m?.doses),
      titulo: m?.tipo === 'entrada'
        ? 'Entrada de sêmen'
        : daBancada ? 'Usado na FIV' : (rotuloDoMotivoDeSemen(m?.motivo) || 'Saída'),
      descricao: rotulo,
      detalhe: m?.destino || m?.observacoes || null,
    });
  }

  return linhas.sort((a, b) => {
    if (a.data !== b.data) return (b.data || '').localeCompare(a.data || '');
    // Empate no dia: entrada antes de saída, que é a ordem em que aconteceram.
    if (a.tipo !== b.tipo) return a.tipo === 'entrada' ? -1 : 1;
    return (a.descricao || '').localeCompare(b.descricao || '');
  });
};

/** O extrato fecha com o saldo? Só quando nenhum embrião saiu. */
export const extratoFecha = ({ saidas = [] } = {}) => (saidas || []).length === 0;

/**
 * Alguma linha do extrato está com data aproximada?
 *
 * A tela e o PDF perguntam isto para decidir se escrevem a nota de pé. Uma nota
 * fixa, que aparece mesmo quando todas as datas são exatas, é uma ressalva que
 * ninguém lê — e que tira a força da mesma frase no dia em que ela importa.
 */
export const temDataAproximada = (linhas = []) =>
  (linhas || []).some(l => l?.aproximada);
